import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { isDesktop } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useHistory } from 'react-router-dom';

import { pushSiteNotification } from 'containers/App/actions';

import {
  sendEmail,
  getEmailStatus,
  deleteEmailStatus,
  getEmailTemplates,
} from 'api/email';

import DropdownButton from 'components/Button/DropdownButton/DropdownButton';
import APDropdown from 'components/Dropdown/APDropdown';
import APModal from 'components/Modal/APModal';
import EmailEditor from 'components/EmailEditor';
import Drawer from 'components/Drawer';
import DeleteConfirmation from 'components/DeleteConfirmation';
import { MobileSm, DefaultSm } from 'components/MediaQueries';
import { DELETE_LEAD_STATUS } from 'containers/Leads/types';
import ArchiveModal from './Leads.ArchiveModal';

/**
 * @description: OptionsButton Component for Leads Table
 * @param {*}
 * @return {*}
 */
export const LeadsOptionsButton = ({ leadId, orgId, contact, active, lostReasons, lostStatus }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.global.userInfo);
  const { integrationsEnabled: isIntegrationsEnabled, personas } = userInfo || {};
  const { pushNotification } = bindActionCreators({ pushNotification: pushSiteNotification }, dispatch);
  const [opened, setOpened] = useState(false);
  const [target, setTarget] = useState(null);
  const [emailEditorOpen, setEmailEditorOpen] = useState(false);
  const [archiveOpened, setArchiveOpened] = useState(false);
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);

  const { integrations = [] } = userInfo;
  const canSendEmail = isIntegrationsEnabled &&
    (personas?.isOrgOwner || personas?.isOrgMember) &&
    integrations.length > 0 && integrations.some((integration) => integration.grantStatus === 'valid');

  const { contactEmail = '', contactNumber = '' } = contact || {};

  const history = useHistory();

  const dismiss = () => {
    setOpened(false);
    target?.classList.add('hover-expose');
  };

  const deleteLeadRecord = async (e) => {
    e?.stopPropagation();
    await dispatch({
      type: DELETE_LEAD_STATUS,
      payload: {
        variables: {
          id: leadId,
        },
      },
    });
    setDeleteConfirmOpened(false);
  };

  const copy = (string) => {
    navigator.clipboard.writeText(string);
  };

  const renderOptions = () => {
    const copyEmail = (e) => {
      e.stopPropagation();
      copy(contactEmail);
      pushNotification({
        message: 'Email copied to clipboard.',
        time: 3000,
      });
      dismiss();
    };

    const copyPhone = (e) => {
      e.stopPropagation();
      copy(contactNumber);
      pushNotification({
        message: 'Phone number copied to clipboard.',
        time: 3000,
      });
      dismiss();
    };

    const archive = (e) => {
      e.stopPropagation();
      setArchiveOpened(true);
      dismiss();
    };

    const deleteItem = (e) => {
      e.stopPropagation();
      setDeleteConfirmOpened(true);
      dismiss();
    };

    const openEmailEditor = (e) => {
      e.stopPropagation();
      setEmailEditorOpen(true);
      dismiss();
    };

    const goToLeadEmailer = () => {
      history.push(`/app/v2/${orgId}/leads/lead/${leadId}/emails`);
    };

    return (
      <>
        {canSendEmail && (
          <button
            className={clsx(
              'button--unstyled',
              'button--menu',
            )}
            onClick={openEmailEditor}
          >
            Create New Email
          </button>
        )}
        {canSendEmail && (
          <button
            className={clsx(
              'button--unstyled',
              'button--menu',
            )}
            onClick={() => goToLeadEmailer()}
          >
            View All Emails
          </button>
        )}
        {contactEmail && (
          <button
            className={clsx(
              'button--unstyled',
              'button--menu',
            )}
            onClick={copyEmail}
          >
            Copy Email Address
          </button>
        )}
        {contactNumber && (
          <button
            className={clsx(
              'button--unstyled',
              'button--menu',
            )}
            onClick={copyPhone}
          >
            Copy Phone Number
          </button>
        )}
        <button
          className={clsx(
            'button--unstyled',
            'button--menu',
          )}
          onClick={archive}
        >
          Mark as Lost
        </button>
        <button
          className={clsx(
            'button--unstyled',
            'button--menu',
          )}
          onClick={deleteItem}
        >
          Delete
        </button>
      </>
    );
  };

  const trigger = (
    <DropdownButton
      className="button--unstyled button--options"
    >
      <span className="fa fa-ellipsis-v" />
    </DropdownButton>
  );

  return (
    <>
      <DefaultSm>
        <APDropdown
          isOpen={opened}
          show={(e) => {
            setOpened(true);
            setTarget(e.currentTarget.parentNode.parentNode);
            e.currentTarget.parentNode.parentNode.classList.remove('hover-expose');
          }}
          dismiss={() => {
            if (opened) {
              setOpened(false);
              setTarget(null);

              if (isDesktop) {
                target.classList.add('hover-expose');
              }
            }
          }}
          triggerButton={trigger}
        >
          {renderOptions()}
        </APDropdown>
      </DefaultSm>
      <MobileSm>
        <Drawer
          isOpen={opened}
          show={() => setOpened(true)}
          dismiss={() => dismiss()}
          position="bottom"
          triggerButton={trigger}
        >
          {renderOptions()}
        </Drawer>
      </MobileSm>
      <ArchiveModal
        leadId={leadId}
        lostReasons={lostReasons}
        lostStatus={lostStatus}
        active={active}
        archiveOpened={archiveOpened}
        setArchiveOpened={setArchiveOpened}
      />
      <div onClick={(e) => e.stopPropagation()}>
        <DeleteConfirmation
          show={deleteConfirmOpened}
          handleClose={(e) => {
            e?.stopPropagation();
            setDeleteConfirmOpened(false);
          }}
          handleConfirm={deleteLeadRecord}
          deleteText="Delete"
          size="xs"
          confirmText="DELETE"
          requireConfirmation
        >
          <p>
            Deleting this lead will remove it forever.
            Type "DELETE" to confirm.
          </p>
        </DeleteConfirmation>
      </div>
      <APModal
        name="email-editor"
        open={emailEditorOpen}
        onClose={() => setEmailEditorOpen(false)}
        shouldCloseOnOverlayClick={false}
      >
        <EmailModal
          orgId={orgId}
          leadId={leadId}
          dismiss={() => setEmailEditorOpen(false)}
        />
      </APModal>
    </>
  );
};

const EmailModal = ({
  orgId,
  leadId,
  dismiss,
}) => {
  let timer;
  const history = useHistory();
  const userInfo = useSelector((state) => state.global.userInfo);
  const leads = useSelector((state) => state.leads);
  const { integrationsEmails: userEmail = [] } = userInfo || {};
  const { leadsById } = leads || {};
  const currentLead = leadsById[leadId];
  const recipient = currentLead?.contactEmail;

  const [preview, setPreview] = useState();
  const [emailState, setEmailState] = useState('');
  const [emailFetchFinished, setEmailFetchFinished] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [mergeFields, setMergeFields] = useState([]);

  const goToLeadEmailer = () => {
    history.push(`/app/v2/${orgId}/leads/lead/${leadId}/emails`);
  };

  // #TODO: These can be refactored into one util function instead of being in both lead record and index.
  // Email helpers
  const formatRecipients = (recipients) => {
    return recipients
      ? recipients.map((r) => ({ email: r }))
      : [];
  };

  const formatAttachments = (attachments) => {
    return attachments.map(attachment => {
      return {
        ...attachment,
        content: attachment.content.split(',')[1],
      };
    });
  };

  const fetchEmailStatus = async (id) => {
    try {
      const response = await getEmailStatus(id);

      if (response.ok) {
        const { status } = response.data;
        if (status === 'sent' || status === 'error') {
          clearInterval(timer);
          await deleteEmailStatus(id);
        }
        return status;
      };
      return 'error';
    } catch {
      return 'error';
    }
  };
  const handleEmailSend = async (payload) => {
    try {
      const message = {
        to: formatRecipients(payload.recipients),
        cc: formatRecipients(payload.cc),
        bcc: formatRecipients(payload.bcc),
        reply_to: formatRecipients(payload.replyTo),
        subject: payload.subject,
        body: payload.message,
        attachments: formatAttachments(payload.attachments),
      };

      if (payload.sender) {
        message.from = formatRecipients([payload.sender]);
      }

      if (payload.replyId) {
        message.reply_to_message_id = payload.replyId;
      }

      const response = await sendEmail({ lead_id: leadId, message });
      if (response.ok && response.data.id) {
        return new Promise((resolve) => {
          timer = setInterval(async () => {
            const status = await fetchEmailStatus(response.data.id);
            if (status === 'sent' || status === 'error') {
              resolve(status);
            }
          }, 1000);
        });
      }
      return 'error';
    } catch {
      return 'error';
    }
  };

  const modalSendEmail = async (payload) => {
    setEmailState('sending');
    const result = await handleEmailSend(payload);
    setEmailState(result);
    if (result === 'error') {
      return;
    }
    setEmailState('');
    goToLeadEmailer();
    dismiss();
  };

  useEffect(() => {
    async function fetchEmailData() {
      const [templates, _mergeFields] = await getEmailTemplates(orgId, leadId);
      if (templates.ok) {
        setEmailTemplates(templates.data);
      }
      if (_mergeFields.ok) {
        setMergeFields(_mergeFields.data);
      }
      setEmailFetchFinished(true);
    }

    fetchEmailData();
  }, []);

  return (
    <EmailEditor
      title='Create a new email.'
      senderEmail={userEmail}
      recipients={recipient ? [recipient] : []}
      allowCopies
      allowBccSender={false}
      onSubmit={modalSendEmail}
      status={emailState}
      onDiscard={dismiss}
      dataLoaded={emailFetchFinished}
      templates={emailTemplates}
      mergeFields={mergeFields}
      allowFormatting
      handlePreviewToggle={setPreview}
      preview={preview}
    />
  );
};
